import React, { useEffect, useMemo, useRef } from 'react';
import { Download, Sparkles } from 'lucide-react';
import Block from './Block';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';

// Backend's `default_storage.url()` returns paths like `/media/...`. Resolve
// those against the configured API origin so the link works from the React
// dev server (localhost:3000) which would otherwise 404 looking up its own
// origin.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

function resolveDownloadUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${API_ORIGIN}${url}`;
  return url;
}

// Predicted values: a binary label is coloured by what it means. When the
// target names an outcome that is bad (churn, fraud, failure, …) the positive
// class is red and the negative green; when it names a good one (conversion,
// renewal, …) the other way round. Only a target whose polarity we can't tell
// keeps the neutral lavender for its positives. Other categorical labels get a
// grey badge, and continuous numbers stay plain text.
const formatPredictedValue = (v) => {
  if (typeof v === 'number') {
    return Number.isInteger(v) ? String(v) : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(v);
};

const POSITIVE = /^(yes|y|churn|churned|fraud|1|true)$/i;
const NEGATIVE = /^(no|n|0|false)$/i;
const BAD_OUTCOME = /churn|fraud|default|fail|defect|cancel|attrit|delinquen|chargeback|risk|breach|outage|complain|return|bounce|dropout|readmi|leav|quit|lapse|claim|loss|accident|incident/i;
const GOOD_OUTCOME = /convert|purchas|buy|renew|retain|win|won|success|click|approv|close|subscri|repa(y|id)|accept|upgrad|engag/i;

// 'bad' | 'good' | null: what a positive prediction means for this target.
// A label that names the bad outcome itself (Churn, Fraud) settles it.
const positiveMeans = (columnKey, value) => {
  if (/^(churn|churned|fraud)$/i.test(String(value))) return 'bad';
  const key = String(columnKey || '');
  if (BAD_OUTCOME.test(key)) return 'bad';
  if (GOOD_OUTCOME.test(key)) return 'good';
  return null;
};

const PredictedValue = ({ value, columnKey }) => {
  const text = formatPredictedValue(value);
  const isNumeric = typeof value === 'number';
  if (isNumeric && value !== 0 && value !== 1) {
    return <span style={{ fontWeight: 600, color: 'var(--foreground)' }}>{text}</span>;
  }
  const isPositive = POSITIVE.test(String(value));
  const isNegative = NEGATIVE.test(String(value));
  if (isPositive || isNegative) {
    const means = positiveMeans(columnKey, value);
    if (means) {
      const good = (means === 'good') === isPositive;
      return <Badge variant={good ? 'success' : 'destructive'}>{text}</Badge>;
    }
    return <Badge variant={isPositive ? 'default' : 'outline'}>{text}</Badge>;
  }
  return <Badge variant="secondary">{text}</Badge>;
};

// The scrollable table itself, factored out so other blocks (e.g. the forecast
// block's switchable table view) can render an identical predictions table.
// `inset` cancels the surrounding Block padding with a negative margin so the
// header row can go edge-to-edge; pass `false` when embedding among other
// content inside a Block.
export const PredictionsTableBody = ({ predictions = [], columns = [], maxHeight = 440, inset = true }) => {
  const scrollerRef = useRef(null);

  useEffect(() => {
    const wrap = scrollerRef.current?.querySelector('.ui-table-wrap');
    if (!wrap) return;
    wrap.style.maxHeight = `${maxHeight}px`;
    wrap.style.overflow = 'auto';
  }, [maxHeight]);

  // Columns the model filled in for at least one row — tinted and marked in the
  // header so the output is easy to tell apart from the input data.
  const predictedKeys = useMemo(() => new Set(
    columns.map((c) => c.key).filter((k) => predictions.some((row) => row[`${k}__is_predicted`])),
  ), [columns, predictions]);

  const edge = inset ? 20 : 12;
  const edgePad = (i) => ({
    ...(i === 0 ? { paddingLeft: edge } : null),
    ...(i === columns.length - 1 ? { paddingRight: edge } : null),
  });
  const tint = 'color-mix(in srgb, var(--muted) 55%, transparent)';

  return (
    <div
      ref={scrollerRef}
      style={{
        borderRadius: inset ? 0 : 10,
        border: inset ? 'none' : '1px solid var(--border)',
        borderTop: '1px solid var(--border)',
        overflow: 'hidden',
      }}
    >
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((c, i) => {
              const predicted = predictedKeys.has(c.key);
              return (
                <TableHead
                  key={c.key}
                  style={{
                    textAlign: c.align || 'left',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    background: predicted ? 'color-mix(in srgb, var(--muted) 100%, var(--card))' : 'var(--card)',
                    color: predicted ? 'var(--foreground)' : undefined,
                    ...edgePad(i),
                  }}
                >
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    flexDirection: c.align === 'right' ? 'row-reverse' : 'row',
                  }}>
                    {predicted && <Sparkles size={13} aria-label="Predicted" />}
                    {c.label}
                  </span>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {predictions.map((row, i) => (
            <TableRow key={i}>
              {columns.map((c, ci) => {
                const v = row[c.key];
                const isPredictedCell = !!row[`${c.key}__is_predicted`] && v != null;
                return (
                  <TableCell
                    key={c.key}
                    className={c.align === 'right' ? 'ui-table__right' : undefined}
                    style={{
                      textAlign: c.align || 'left',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                      background: predictedKeys.has(c.key) ? tint : undefined,
                      ...edgePad(ci),
                    }}
                  >
                    {isPredictedCell ? (
                      <PredictedValue value={v} columnKey={c.key} />
                    ) : c.key === 'confidence' && typeof v === 'number' ? (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Progress value={v * 100} style={{ width: 56, height: 4 }} />
                        <span className="q-muted q-num" style={{ fontSize: 13.5 }}>
                          {(v * 100).toFixed(0)}%
                        </span>
                      </div>
                    ) : (
                      v == null ? <span className="q-muted">—</span> : String(v)
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const PredictionsTable = ({ blockData = {} }) => {
  const predictions = blockData.predictions || [];
  const columns = blockData.columns || [];
  const totalRows = blockData.total_rows ?? predictions.length;
  const nPredicted = blockData.n_predicted;
  const subtitle = blockData.subtitle;
  const downloadUrl = resolveDownloadUrl(blockData.download_url);

  const description = nPredicted != null
    ? `${Number(nPredicted).toLocaleString()} of ${Number(totalRows).toLocaleString()} rows predicted`
    : `${Number(totalRows).toLocaleString()} rows`;

  return (
    <Block
      icon="table-2"
      title={blockData.title || 'Predictions'}
      description={description}
      flush
      badge={downloadUrl && (
        <Button variant="outline" size="sm" asChild>
          <a href={downloadUrl} download>
            <Download aria-hidden />
            Download Excel
          </a>
        </Button>
      )}
      footer={(
        <span className="q-muted">
          {subtitle || `Showing ${predictions.length.toLocaleString()} of ${Number(totalRows).toLocaleString()}`}
        </span>
      )}
    >
      <PredictionsTableBody predictions={predictions} columns={columns} />
    </Block>
  );
};

export default PredictionsTable;
